import _ from 'underscore';
import Promise from 'bluebird';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { BaseSchemaProcessor } from './base-schema-processor';
import { lambdaConvertForArrayTypes } from '../util/update-odata-options-for-array-type';

class CampaignTypesDataSource {
  constructor(i18n) {
    this.disableSearch = true;
    this.campaignTypes = [{
      Id: 'Default',
      Name: i18n.getString(_TL_('Search')),
    }, {
      Id: 'Shopping',
      Name: i18n.getString(_TL_('Shopping')),
    }];
  }

  get(_viewFilter, searchFilter) {
    if (!searchFilter) {
      return Promise.resolve(this.campaignTypes);
    }

    const searchFilterLowerCase = searchFilter.toLowerCase();
    const typesToReturn =
      _.select(
        this.campaignTypes,
        type => type.Name.toLowerCase().indexOf(searchFilterLowerCase) === 0
      );

    return Promise.resolve(typesToReturn);
  }

  getByIds() {
    return Promise.resolve(this.campaignTypes);
  }
}

class DeviceTypesDataSource {
  constructor(i18n) {
    this.disableSearch = true;
    this.deviceTypes = [{
      Id: 'Smartphones',
      Name: i18n.getString(_TL_('Smartphones')),
    }, {
      Id: 'Tablets',
      Name: i18n.getString(_TL_('Tablets')),
    }, {
      Id: 'Computers',
      Name: i18n.getString(_TL_('Computers')),
    }];
  }

  get(_viewFilter, searchFilter) {
    if (!searchFilter) {
      return Promise.resolve(this.deviceTypes);
    }

    const searchFilterLowerCase = searchFilter.toLowerCase();
    const typesToReturn =
      _.select(
        this.deviceTypes,
        type => type.Name.toLowerCase().indexOf(searchFilterLowerCase) === 0
      );

    return Promise.resolve(typesToReturn);
  }

  getByIds() {
    return Promise.resolve(this.deviceTypes);
  }
}

export class SeasonalityAdjustmentSchemaProcessor extends BaseSchemaProcessor {
  updateOdataOptions(options) {
    const optionsCopy = options || { custom: {} };

    _.extendOwn(optionsCopy.custom, {
      CampaignTypeFilter: {
        containsall: lambdaConvertForArrayTypes('and', 'any', 'eq', 'CampaignTypeFilter', 'CampaignType'),
        containsany: lambdaConvertForArrayTypes('or', 'any', 'eq', 'CampaignTypeFilter', 'CampaignType'),
        containsnone: lambdaConvertForArrayTypes('and', 'all', 'ne', 'CampaignTypeFilter', 'CampaignType'),
      },
      DeviceTypeFilter: {
        containsall: lambdaConvertForArrayTypes('and', 'any', 'eq', 'DeviceTypeFilter', 'DeviceType'),
        containsany: lambdaConvertForArrayTypes('or', 'any', 'eq', 'DeviceTypeFilter', 'DeviceType'),
        containsnone: lambdaConvertForArrayTypes('and', 'all', 'ne', 'DeviceTypeFilter', 'DeviceType'),
      },
    });
    return optionsCopy;
  }

  updateEntitySchemaForFeatures(entitySchema) {
    const entitySchemaCopy = cloneDeep(entitySchema);
    const operatorDisplayNameMap = {
      containsall: this.passedInI18n.getString(_TL_('contains all')),
      containsany: this.passedInI18n.getString(_TL_('contains any')),
      containsnone: this.passedInI18n.getString(_TL_('contains none')),
    };
    const operators = ['containsall', 'containsany', 'containsnone'];

    entitySchemaCopy.properties.CampaignTypeFilter = {
      property: 'CampaignTypeFilter',
      name: this.passedInI18n.getString(_TL_('Campaign type')),
      title: this.passedInI18n.getString(_TL_('Campaign type')),
      operatorDisplayNameMap,
      operators,
      type: 'id',
      filterable: true,
      addtionalParams: {
        dataSource: new CampaignTypesDataSource(this.passedInI18n),
        selectorOptions: {
          keyMap: {
            Selector_Filter_Placeholder: () => this.passedInI18n.getString(_TL_('Campaign type')),
          },
        },
      },
    };

    entitySchemaCopy.properties.DeviceTypeFilter = {
      property: 'DeviceTypeFilter',
      name: this.passedInI18n.getString(_TL_('Device type')),
      title: this.passedInI18n.getString(_TL_('Device type')),
      operatorDisplayNameMap,
      operators,
      type: 'id',
      filterable: true,
      addtionalParams: {
        dataSource: new DeviceTypesDataSource(this.passedInI18n),
        selectorOptions: {
          keyMap: {
            Selector_Filter_Placeholder: () => this.passedInI18n.getString(_TL_('Device type')),
          },
        },
      },
    };

    return entitySchemaCopy;
  }
}

/**
 * In Odata model, DataExclusion is a special SeasonalityAdjustment.
 *
 * This is a string restriction. However, there are some difference, and we want to reuse most of the logic.
 *
 * So use inheritage from "SeasonalityAdjustmentSchemaProcessor" rather than "BaseSchemaProcessor"
 */
export class DataExclusionSchemaProcessor extends SeasonalityAdjustmentSchemaProcessor {
  updateEntitySchemaForFeatures(entitySchema) {
    const entitySchemaCopy = super.updateEntitySchemaForFeatures(entitySchema);

    if (entitySchemaCopy.properties.AdjustmentPercentage) {
      entitySchemaCopy.properties.AdjustmentPercentage = {
        ...entitySchemaCopy.properties.AdjustmentPercentage,
        filterable: false,
      };
    }

    return entitySchemaCopy;
  }
}
